import { Icon, useDefenceVariant, useStateNarration, tokens } from '@/design';

export interface DefenceChipProps {
  /** Override ARIA label for testing scenarios */
  ariaLabel?: string;
}

/**
 * DefenceChip — reusable defence variant indicator per GVW-176.
 *
 * Extracted from AppShell's inline defence banner JSX to enable reuse
 * across surfaces. Reads `useDefenceVariant()` state and renders a
 * persistent header chip with:
 * - `policy` icon (shield visual cue per brief)
 * - "Defence variant" label text
 * - Live region narration for accessibility
 * - Non-color cues per DESIGN_POLICY.md A4 rule
 *
 * Only renders when defence variant is active. Hidden when inactive.
 */
export function DefenceChip({ ariaLabel }: DefenceChipProps): JSX.Element | null {
  const defenceVariant = useDefenceVariant();
  const defenceNarration = useStateNarration({
    key: 'gvw-176-defence-banner',
    severity: 'error',
    text: 'Local-only mode active. No external services contacted.',
  });

  if (!defenceVariant.active) {
    return null;
  }

  return (
    <div
      data-gvw-id="GVW-176"
      role={defenceNarration.role}
      aria-live={defenceNarration.ariaLive}
      aria-atomic="true"
      aria-label={
        ariaLabel ||
        'Defence variant; Air-gapped cluster; outbound traffic blocked'
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        borderBottom: tokens.border.thin,
        borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.danger}`,
        background: tokens.color.surface[2],
      }}
    >
      <Icon name="security" size={20} decorative />
      <strong>Defence variant</strong>
      <span>Local-only mode active. No external services contacted.</span>
      {defenceVariant.reason ? (
        <span style={{ color: tokens.color.ink.secondary }}>
          ({defenceVariant.reason})
        </span>
      ) : null}
    </div>
  );
}